/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from 'vue';
import Router from 'vue-router';
import { userService } from './appContainer';
import store from './store/store.js';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '/',
      component: () => import('./layouts/main/Main.vue'),
      props: true,
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '',
          name: 'home',
          component: () => import('./views/Home.vue'),
          props: true,
        },
        {
          path: 'change-password',
          name: 'change-password',
          component: () => import('./views/pages/ChangePassword.vue'),
          props: true,
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('./views/pages/Profile.vue'),
        },
        {
          path: 'table',
          name: 'aggregate-table',
          component: () => import('./views/agGridTables/AgGridTable.vue'),
          props: (route) => ({ projectId: route.params.projectId }),
        },
        {
          path: 'capacities',
          name: 'capacities',
          component: () => import('./views/agGridTables/CapacitiesTable.vue'),
        },
        {
          path: 'timesheet',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'timesheet',
          component: () => import('./views/agGridTables/EmployeeTimesheet.vue'),
        },
        {
          path: 'settings',
          props: (route) => ({ companyId: route.params.companyId }),

          name: 'settings',
          component: () => import('./views/Settings.vue'),
        },
        {
          path: 'employees',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'employees',
          component: () => import('./views/Employees.vue'),
        },
        {
          path: 'projects',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'projects',
          component: () => import('./views/Projects.vue'),
        },
        {
          path: 'work-reports',
          props: (route) => ({
            projectId: route.params.projectId,
            projectName: route.params.projectName,
          }),
          name: 'work-reports',
          component: () => import('./views/agGridTables/WorkReportsTable.vue'),
        },
      ],
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('./layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: '/login',
          name: 'page-login',
          component: () => import('./views/pages/Login.vue'),
        },
        {
          path: '/forgotten-password',
          name: 'forgotten-password',
          component: () => import('./views/pages/ForgottenPassword.vue'),
        },
        {
          path: '/register',
          name: 'register',
          component: () => import('./views/pages/Register.vue'),
        },
        {
          path: '/reset-password',
          name: 'reset-password',
          component: () => import('./views/pages/ResetPassword.vue'),
          props: (route) => ({ token: route.params.token }),
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('./views/pages/Error404.vue'),
        },
      ],
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404',
    },
  ],
});

const nonRedirectedPaths = ['page-login', 'register', 'forgotten-password', 'reset-password'];
const companyTabsPermissions = [
  {
    path: '/table',
    permission: 'cashflow_view',
  },
  {
    path: '/capacities',
    permission: 'capacities_view',
  },
  {
    path: '/employees',
    permission: 'people_view',
  },
  {
    path: '/projects',
    permission: 'projects_view',
  },
  {
    path: '/timesheet',
    permission: 'timesheets_view',
  },
  {
    path: '/settings',
  },
];

router.beforeEach(async (to, _from, next) => {
  try {
    const { userInfo } = await userService.userIsLoggedIn();
    store.dispatch('updateUserInfo', userInfo);
    await store.dispatch('updateIsLoggedIn', true);

    const companyTabObject = companyTabsPermissions.find((t) => t.path === to.path);

    // Check permission for the tab
    if (
      Object.keys(store.state.user.companies).some(store.getters.isAdmin) ||
      companyTabObject === undefined ||
      (companyTabObject &&
        store.getters.hasPermission(to.query.projectId, companyTabObject.permission))
    ) {
      next();
    } else {
      next('/');
    }
  } catch (e) {
    if (e === 401) {
      await store.dispatch('updateIsLoggedIn', false);
    }
    if (!nonRedirectedPaths.includes(to.name) && e === 401) {
      next('/login');
    }
    next();
  }
});
router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

export default router;
